/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-restricted-syntax */
import React, { useEffect, useRef, useMemo } from "react";
import { format, isSameDay, differenceInMinutes, parseISO } from "date-fns";
import { DayColumn, CalendarEvent } from "../types";
import * as S from "../styles";

interface TimeGridProps {
  dayColumns: DayColumn[];
  onEventClick: (event: CalendarEvent, element: HTMLElement) => void;
  onTimeSlotClick: (date: Date, hour: number, minutes: number) => void;
}

const HOUR_HEIGHT = 60; // px por hora
const START_HOUR = 0;
const END_HOUR = 24;
const HOURS = Array.from(
  { length: END_HOUR - START_HOUR },
  (_, i) => i + START_HOUR
);

const formatHour = (hour: number): string => {
  if (hour === 0) return "";
  const h = hour.toString().padStart(2, "0");
  return `${h}`;
};

// Calcula sobreposições entre eventos
const getEventLayout = (events: CalendarEvent[]) => {
  const timedEvents = events
    .filter((e) => !e.isAllDay && e.start?.dateTime && e.end?.dateTime)
    .sort((a, b) => {
      const startA = new Date(a.start!.dateTime!).getTime();
      const startB = new Date(b.start!.dateTime!).getTime();
      return startA - startB;
    });

  const layouts: Map<
    string,
    { left: number; width: number; top: number; height: number }
  > = new Map();

  const groups: CalendarEvent[][] = [];
  let currentGroup: CalendarEvent[] = [];
  let groupEnd = 0;

  for (const event of timedEvents) {
    const eventStart = new Date(event.start!.dateTime!).getTime();
    const eventEnd = new Date(event.end!.dateTime!).getTime();

    if (currentGroup.length === 0 || eventStart < groupEnd) {
      currentGroup.push(event);
      groupEnd = Math.max(groupEnd, eventEnd);
    } else {
      groups.push(currentGroup);
      currentGroup = [event];
      groupEnd = eventEnd;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  for (const group of groups) {
    const columns: CalendarEvent[][] = [];

    for (const event of group) {
      const eventStart = new Date(event.start!.dateTime!).getTime();
      let placed = false;

      for (let col = 0; col < columns.length; col++) {
        const lastInCol = columns[col][columns[col].length - 1];
        const lastEnd = new Date(lastInCol.end!.dateTime!).getTime();

        if (eventStart >= lastEnd) {
          columns[col].push(event);
          placed = true;
          break;
        }
      }

      if (!placed) {
        columns.push([event]);
      }
    }

    const totalCols = columns.length;
    columns.forEach((col, colIdx) => {
      for (const event of col) {
        const start = new Date(event.start!.dateTime!);
        const end = new Date(event.end!.dateTime!);
        const topMinutes = start.getHours() * 60 + start.getMinutes();
        const durationMinutes = Math.max(differenceInMinutes(end, start), 15);

        layouts.set(event.id, {
          top: topMinutes,
          height: durationMinutes,
          left: (colIdx / totalCols) * 100,
          width: 100 / totalCols,
        });
      }
    });
  }

  return layouts;
};

const TimeGrid: React.FC<TimeGridProps> = ({
  dayColumns,
  onEventClick,
  onTimeSlotClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);

  // Scroll para hora atual no primeiro render
  useEffect(() => {
    if (containerRef.current && !scrolledRef.current) {
      const now = new Date();
      const scrollTo = (now.getHours() - 1) * HOUR_HEIGHT;
      containerRef.current.scrollTop = Math.max(0, scrollTo);
      scrolledRef.current = true;
    }
  }, []);

  // Indicador de hora atual
  const currentTimeTop = useMemo(() => {
    const now = new Date();
    return now.getHours() * HOUR_HEIGHT + (now.getMinutes() / 60) * HOUR_HEIGHT;
  }, []);

  // Atualiza o indicador a cada minuto
  const [, setTick] = React.useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const todayColumnIndex = dayColumns.findIndex((col) => col.isToday);

  return (
    <S.TimeGridContainer ref={containerRef}>
      <S.TimeGridInner $columns={dayColumns.length}>
        {/* Coluna de horas */}
        <S.TimeGutter>
          {HOURS.map((hour) => (
            <S.TimeLabel key={hour}>{formatHour(hour)}</S.TimeLabel>
          ))}
        </S.TimeGutter>

        {/* Colunas dos dias */}
        {dayColumns.map((col, colIdx) => {
          const layouts = getEventLayout(col.events);

          return (
            <S.DayColumnContainer key={col.date.toISOString()}>
              {/* Linhas de hora */}
              {HOURS.map((hour) => (
                <S.HourRow
                  key={hour}
                  onClick={() => onTimeSlotClick(col.date, hour, 0)}
                />
              ))}

              {/* Indicador de hora atual (só na coluna de hoje) */}
              {col.isToday && (
                <S.CurrentTimeLine $top={currentTimeTop}>
                  <S.CurrentTimeDot />
                  <S.CurrentTimeLineBar />
                </S.CurrentTimeLine>
              )}

              {/* Eventos */}
              {col.events
                .filter((e) => !e.isAllDay && e.start?.dateTime)
                .map((event) => {
                  const layout = layouts.get(event.id);
                  if (!layout) return null;

                  const start = new Date(event.start!.dateTime!);
                  const end = event.end?.dateTime
                    ? new Date(event.end.dateTime)
                    : start;

                  return (
                    <S.EventBlock
                      key={event.id}
                      $top={layout.top}
                      $height={layout.height}
                      $showAs={event.showAs}
                      $isCancelled={event.isCancelled}
                      $left={layout.left}
                      $width={layout.width}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event, e.currentTarget);
                      }}
                      title={event.subject || "Sem título"}
                    >
                      <S.EventTitle>
                        {event.isCancelled ? "Cancelado: " : ""}
                        {event.subject || "Sem título"}
                      </S.EventTitle>
                      {layout.height > 30 && (
                        <S.EventTime>
                          {format(start, "HH:mm")} - {format(end, "HH:mm")}
                        </S.EventTime>
                      )}
                    </S.EventBlock>
                  );
                })}
            </S.DayColumnContainer>
          );
        })}
      </S.TimeGridInner>
    </S.TimeGridContainer>
  );
};

export default TimeGrid;
